import { Trash2, ChevronDown, ChevronUp, Inbox, Play, TimerReset, Pin, Clock, Reply, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import StatusBadge from "./StatusBadge";

export default function EmailCard({
  email, expanded, selected, onToggleSelect, onToggle, onCopy, onDelete,
  onExtend, onExpire, onView, onUse, onTogglePin, onToggleReply, replyEnabled, formatDate, formatRemainingTime,
  durations, users = [], assignEmailToUser, unassignEmailFromUser, onRefreshEmails
}) {
  return (
    <div className={`border rounded-xl bg-zinc-900/30 backdrop-blur-sm overflow-hidden transition-colors duration-200 ${selected ? "border-cyan-500/40 bg-cyan-500/5 shadow-[0_0_12px_rgba(34,211,238,0.04)]" : email.pinned ? "border-amber-500/25 shadow-[0_0_12px_rgba(245,158,11,0.04)]" : "border-zinc-800/50 hover:border-zinc-700/50"}`}>
      <div className="p-3 flex items-center gap-2">
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            className="border-zinc-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
          />
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggle}>
          <div className="flex items-center gap-2 mb-1">
            {email.pinned && <Pin className="w-3 h-3 text-amber-400 fill-current flex-shrink-0" />}
            <span
              onClick={(e) => { e.stopPropagation(); onCopy(); }}
              className="font-mono text-xs text-white truncate hover:text-cyan-400 transition-colors"
            >
              {email.full_address}
            </span>
            {email.owner && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono font-medium shrink-0 ${
                email.owner.role === "super_admin"
                  ? "bg-red-500/10 text-red-400 border border-red-500/20"
                  : email.owner.role === "admin"
                  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                  : "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
              }`}>
                {email.owner.role === "super_admin" ? "Super Admin" : email.owner.role === "admin" ? "Admin" : "Utilisateur"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={email.status} />
            <span className={`font-mono text-xs ${
              email.remaining_seconds === -1 ? "text-amber-400" :
              email.remaining_seconds <= 0 ? "text-red-400" : "text-green-400"
            }`}>
              {formatRemainingTime(email.remaining_seconds)}
            </span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t border-zinc-800/50 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-zinc-500">Messages:</span>
            <span className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-[10px] font-mono px-1.5 py-0.5 rounded">
              {email.message_count ?? 0}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-zinc-500">Durée:</span>
            <span className="text-zinc-300 font-mono">
              {email.lifetime === "expired" ? "Expiré" : email.lifetime === "permanent" ? "Permanent" : email.lifetime || "N/A"}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-zinc-500">Créé le:</span>
            <span className="text-zinc-300">{formatDate(email.created_at)}</span>
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button
              onClick={onTogglePin}
              variant="outline"
              size="sm"
              className={`text-xs ${email.pinned ? "text-amber-400 border-amber-500/30 hover:bg-amber-500/10" : "text-zinc-400 border-zinc-700 hover:bg-amber-500/10 hover:text-amber-400"}`}
              data-testid={`pin-email-mobile-${email.id}`}
            >
              <Pin className={`w-3 h-3 mr-1 ${email.pinned ? "fill-current" : ""}`} />
              {email.pinned ? "Désépingler" : "Épingler"}
            </Button>
            <Button
              onClick={onToggleReply}
              variant="outline"
              size="sm"
              className={`text-xs ${replyEnabled ? "text-blue-400 border-blue-500/30 hover:bg-blue-500/10" : "text-zinc-400 border-zinc-700 hover:bg-blue-500/10 hover:text-blue-400"}`}
            >
              <Reply className={`w-3 h-3 mr-1 ${replyEnabled ? "fill-current" : ""}`} />
              {replyEnabled ? "Réponse ON" : "Réponse OFF"}
            </Button>
            {assignEmailToUser && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`text-xs ${email.owner ? "text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10" : "text-zinc-400 border-zinc-700 hover:bg-cyan-500/10 hover:text-cyan-400"}`}
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    Assigner
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="bg-zinc-900 border-zinc-700 p-1 w-auto min-w-[160px]" align="start">
                  <div className="flex flex-col max-h-48 overflow-y-auto">
                    <p className="text-[10px] text-zinc-500 uppercase px-3 py-1 font-medium">Assigner à</p>
                    {users.map(u => {
                      const isAssigned = email.owner?.username === u.username;
                      return (
                        <button
                          key={u.id}
                          onClick={async () => {
                            if (isAssigned) {
                              await unassignEmailFromUser(u.id, email.id);
                            } else {
                              await assignEmailToUser(u.id, email.id);
                            }
                            onRefreshEmails?.();
                          }}
                          className={`text-left text-xs font-mono px-3 py-1.5 rounded transition-colors flex items-center gap-2 ${
                            isAssigned
                              ? "text-cyan-400 bg-cyan-500/10"
                              : "text-zinc-300 hover:bg-cyan-500/10 hover:text-cyan-400"
                          }`}
                        >
                          <span className="truncate">{u.username}</span>
                          {isAssigned && <span className="text-[9px] text-cyan-500/60 shrink-0">assigné</span>}
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <Button onClick={onView} variant="outline" size="sm" className="text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10 text-xs">
              <Inbox className="w-3 h-3 mr-1" />
              Voir
            </Button>
            {email.status !== "expired" && (
              <Button onClick={onUse} variant="outline" size="sm" className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 text-xs">
                <Play className="w-3 h-3 mr-1" />
                Utiliser
              </Button>
            )}
            {email.status !== "permanent" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-green-400 border-green-500/30 hover:bg-green-500/10 text-xs">
                    <TimerReset className="w-3 h-3 mr-1" />
                    Prolonger
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="bg-zinc-900 border-zinc-700 p-1 w-auto min-w-[140px]" align="start">
                  <div className="flex flex-col">
                    {(durations || []).map(d => (
                      <button key={d.key} onClick={() => onExtend(d.key)}
                        className="text-left text-xs font-mono px-3 py-1.5 text-zinc-300 hover:bg-green-500/10 hover:text-green-400 rounded transition-colors">
                        +{d.label}
                      </button>
                    ))}
                    <button onClick={() => onExtend("permanent")}
                      className="text-left text-xs font-mono px-3 py-1.5 text-amber-400 hover:bg-amber-500/10 rounded transition-colors">
                      Permanent
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {email.status !== "expired" && (
              <Button onClick={onExpire} variant="outline" size="sm" className="text-orange-400 border-orange-500/30 hover:bg-orange-500/10 text-xs">
                <Clock className="w-3 h-3 mr-1" />
                Expirer
              </Button>
            )}
            {onDelete && (
            <Button onClick={onDelete} variant="outline" size="sm" className="text-red-400 border-red-500/30 hover:bg-red-500/10 text-xs">
              <Trash2 className="w-3 h-3 mr-1" />
              Supprimer
            </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
